import { ref } from 'vue';

import { defineStore } from 'pinia';

import type { GlobalDialogData } from '@/types/dialogs';

/**
 * Dialog state management store.
 *
 * Manages the app-level dialog states including the global notification dialog
 * and context selection dialogs (depot, tenant).
 *
 * ## Global Notification Dialog
 *
 * Triggered via `toggleGlobalDialog()` and rendered by `GlobalModalDialog.vue`.
 * Supports both simple notifications (via `useNotification()` composable) and
 * full-featured dialogs with callbacks (direct store access).
 *
 * See `src/types/dialogs.ts` (`GlobalDialogData`) for the data shape.
 *
 * ### Usage
 *
 * Simple notification:
 * ```js
 * const { showSuccessAlert } = useNotification();
 * showSuccessAlert('Operation complete');
 * ```
 *
 * Full dialog with callback:
 * ```js
 * const { toggleGlobalDialog } = useDialogsStore();
 * toggleGlobalDialog({
 *   type: 'warning',
 *   msg: 'Are you sure?',
 *   submitText: 'Confirm',
 *   callback: (isSubmit) => { if (isSubmit) performAction(); }
 * });
 * ```
 *
 * Close:
 * ```js
 * toggleGlobalDialog(null);
 * ```
 */
const useDialogsStore = defineStore('dialogs', () => {
  // State
  const globalDialog = ref<GlobalDialogData | null>(null);
  const showDepotSelectionDialog = ref(false);
  const showTenantSelectionDialog = ref(false);

  // Actions
  /**
   * Toggle depot selection dialog visibility.
   */
  const toggleDepotSelectionDialog = (isOpened: boolean): void => {
    showDepotSelectionDialog.value = isOpened;
  };

  /**
   * Toggle tenant selection dialog visibility.
   */
  const toggleTenantSelectionDialog = (isOpened: boolean): void => {
    showTenantSelectionDialog.value = isOpened;
  };

  /**
   * Update global notification dialog state.
   *
   * @example
   * // Success notification
   * toggleGlobalDialog({ type: 'success', msg: 'Saved' });
   * @example
   * // Confirmation dialog
   * toggleGlobalDialog({
   *   type: 'warning',
   *   msg: 'Delete?',
   *   submitText: 'Delete',
   *   callback: (ok) => { if (ok) deleteItem(); }
   * });
   * @example
   * // Close dialog
   * toggleGlobalDialog(null);
   */
  const toggleGlobalDialog = (dialogData: GlobalDialogData | null): void => {
    globalDialog.value = dialogData;
  };

  return {
    globalDialog,
    showDepotSelectionDialog,
    showTenantSelectionDialog,
    toggleDepotSelectionDialog,
    toggleTenantSelectionDialog,
    toggleGlobalDialog
  };
});

export default useDialogsStore;
